import React, { useEffect, useState } from 'react';
import { Document, Font, Image, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import LatoRegular from "../../utils/fonts/Lato-Regular.ttf";
import LatoBold from '../../utils/fonts/Lato-Bold.ttf';
import moment from 'moment';
import { formatNumber } from '../../utils/Utils';

const GoodsReceivedPDF = ({ page_data, tenant_info }) => {
 const [pdfData, setPDFData] = useState(page_data);

 Font.register({
  family: 'Lato',
  fonts: [{ src: LatoRegular }, { src: LatoBold, fontWeight: 'bold' }]
 });

 useEffect(() => {
  setPDFData(page_data);
 }, [page_data]);

 const styles = StyleSheet.create({
  page: {
   padding: 25,
   paddingBottom: 60, // Added bottom padding for footer
   fontFamily: "Lato",
   backgroundColor: '#f9f9f9',
   fontSize: 9, // Reduced base font size
   lineHeight: 1.2 // Tighter line height
  },
  header: {
   flexDirection: 'row',
   alignItems: 'flex-start',
   justifyContent: 'space-between',
   marginBottom: 15,
   borderBottom: '1px solid #e1e1e1',
   paddingBottom: 15
  },
  logoContainer: {
   flex: 1
  },
  logo: {
   width: "auto",
   height: 70
  },
  titleContainer: {
   flex: 2,
   textAlign: 'center',
   paddingTop: 10
  },
  companyName: {
   fontSize: 16, // Slightly smaller
   fontWeight: "bold",
   color: '#2c3e50'
  },
  documentTitle: {
   fontSize: 14, // Slightly smaller
   fontWeight: "semibold",
   marginTop: 10,
   color: '#3498db'
  },
  infoContainer: {
   flex: 1,
   textAlign: 'right'
  },
  infoText: {
   fontSize: 8,
   color: '#7f8c8d',
   marginBottom: 2
  },
  detailsContainer: {
   flexDirection: 'row',
   flexWrap: 'wrap',
   justifyContent: 'space-between',
   borderWidth: 1,
   borderColor: "#e1e1e1",
   borderRadius: 6,
   padding: 12,
   marginBottom: 20,
   backgroundColor: '#ffffff',
   boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
  },
  detailColumn: {
   width: '48%',
   marginBottom: 8
  },
  detailRow: {
   flexDirection: "row",
   justifyContent: "space-between",
   marginBottom: 6
  },
  detailLabel: {
   fontWeight: "bold",
   fontSize: 9,
   color: '#7f8c8d'
  },
  detailValue: {
   fontSize: 9,
   color: '#2c3e50'
  },
  table: {
   borderWidth: 1,
   borderColor: "#e1e1e1",
   borderRadius: 6,
   overflow: "hidden",
   marginBottom: 25,
   backgroundColor: '#ffffff',
   boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
  },
  tableHeader: {
   flexDirection: "row",
   backgroundColor: "#3498db",
   padding: 6
  },
  headerCell: {
   textAlign: "center",
   fontWeight: "bold",
   fontSize: 8, // Smaller font for header
   color: 'white'
  },
  tableRow: {
   flexDirection: "row",
   borderBottomWidth: 1,
   borderColor: "#e1e1e1",
   padding: 6,
   minHeight: 20
  },
  rowCell: {
   textAlign: "center",
   fontSize: 8, // Smaller font for table cells
   color: '#34495e'
  },
  totalRow: {
   flexDirection: "row",
   backgroundColor: "#f8f9fa",
   padding: 6,
   borderTopWidth: 1,
   borderColor: "#e1e1e1"
  },
  totalLabel: {
   width: '55%',
   textAlign: "right",
   paddingRight: '5%',
   fontWeight: "bold",
   fontSize: 9,
   color: '#2c3e50'
  },
  totalValueCell: {
   width: '11.11%',
   textAlign: "center",
   fontWeight: "bold",
   fontSize: 9,
   color: '#e74c3c'
  },
  footer: {
   position: 'absolute',
   bottom: 20,
   left: 25,
   right: 25,
   textAlign: "center",
   fontSize: 8,
   color: '#95a5a6',
   paddingTop: 8,
   borderTop: '1px solid #eee'
  },
  valueHighlight: {
   color: '#27ae60',
   fontWeight: 'bold'
  },
  unitValue: {
   color: '#3498db',
   fontWeight: 'bold'
  },
  pageNumber: {
   position: 'absolute',
   bottom: 22,
   right: 25,
   fontSize: 8,
   color: '#95a5a6'
  }
 });

 let receivedItems = pdfData?.products || [];
 let totalUnits = receivedItems.reduce((acc, item) => acc + (item.units || 0), 0);
 let totalValue = receivedItems.reduce((acc, item) => acc + (item.total_value || 0), 0);

 // Group details into two columns for better space utilization
 const detailGroups = [
  [
   { label: "Reference No:", value: pdfData?.goods_received_ref_no },
   { label: "Supplier:", value: pdfData?.supplier },
   { label: "Driver Name:", value: pdfData?.truck_driver_name },
   { label: "Trailer 1:", value: pdfData?.trailer_one }
  ],
  [
   { label: "Date Created:", value: pdfData?.inserted_at ? moment(pdfData?.inserted_at).format("YYYY-MM-DD HH:mm:ss") : "--" },
   { label: "Transporter:", value: pdfData?.transporter },
   { label: "License Plate:", value: pdfData?.license_plate },
   { label: "Trailer 2:", value: pdfData?.trailer_two },
   { label: "Invoice Ref No:", value: pdfData?.invoice_ref_no }
  ]
 ];

 return (
  <Document title={pdfData?.goods_received_ref_no ? `Goods Received - ${pdfData?.goods_received_ref_no}` : "Goods Received"}>
   <Page size="A4" orientation="landscape" style={styles.page} wrap>
    {/* Header with logo and title side by side */}
    <View style={styles.header} fixed>
     <View style={styles.logoContainer}>
      {tenant_info?.tenant_logo && <Image style={styles.logo} src={tenant_info?.tenant_logo} />}
     </View>

     <View style={styles.titleContainer}>
      <Text style={styles.companyName}>{tenant_info?.name}</Text>
      <Text style={styles.documentTitle}>GOODS RECEIVED NOTE</Text>
     </View>

     <View style={styles.infoContainer}>
      <Text style={styles.infoText}>Date: {new Date().toLocaleDateString()}</Text>
      <Text style={styles.infoText}>Ref: {pdfData?.goods_received_ref_no || "--"}</Text>
     </View>
    </View>

    {/* Goods Received Details - Two Column Layout */}
    <View style={styles.detailsContainer} fixed>
     {detailGroups.map((group, groupIndex) => (
      <View key={groupIndex} style={styles.detailColumn}>
       {group.map((item, index) => (
        <View key={index} style={styles.detailRow}>
         <Text style={styles.detailLabel}>{item.label}</Text>
         <Text style={styles.detailValue}>{item.value || "--"}</Text>
        </View>
       ))}
      </View>
     ))}
    </View>

    {/* Received Items Table */}
    {receivedItems.length > 0 && (
     <View style={styles.table}>
      {/* Table Header */}
      <View style={styles.tableHeader}>
       {["Product", "Category", "Type", "Color", "Size", "Units", "Unit Value", "Total Value", "Warehouse"].map((header, index) => (
        <Text key={index} style={[styles.headerCell, { width: '11.11%' }]}>{header}</Text>
       ))}
      </View>

      {/* Table Rows */}
      {receivedItems.map((item, index) => {
       const unitValue = item.total_value && item.units ? (item.total_value / item.units) : null;
       return (
        <View key={index} style={[styles.tableRow, { backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }]}>
         <Text style={[styles.rowCell, { width: '11.11%' }]}>{item.product_name || "--"}</Text>
         <Text style={[styles.rowCell, { width: '11.11%' }]}>{item.category || "--"}</Text>
         <Text style={[styles.rowCell, { width: '11.11%' }]}>{item.product_type || "--"}</Text>
         <Text style={[styles.rowCell, { width: '11.11%' }]}>{item.color || "--"}</Text>
         <Text style={[styles.rowCell, { width: '11.11%' }]}>{item.size || "--"}</Text>
         <Text style={[styles.rowCell, { width: '11.11%' }]}>{item.units || "--"}</Text>
         <Text style={[styles.rowCell, { width: '11.11%', ...styles.unitValue }]}>
          {unitValue ? `K${formatNumber(unitValue.toFixed(2))}` : "--"}
         </Text>
         <Text style={[styles.rowCell, { width: '11.11%', ...styles.valueHighlight }]}>
          {item.total_value ? `K${formatNumber(item.total_value.toFixed(2))}` : "--"}
         </Text>
         <Text style={[styles.rowCell, { width: '11.11%' }]}>{item.warehouse_name || "--"}</Text>
        </View>
       );
      })}

      {/* Total Row - Fixed alignment */}
      <View style={styles.totalRow}>
       <Text style={styles.totalLabel}>Total:</Text>
       <Text style={styles.totalValueCell}>{totalUnits || "--"}</Text>
       <View style={styles.totalValueCell}>
        {/* Empty cell for unit value */}
       </View>
       <Text style={styles.totalValueCell}>
        {totalValue ? `K${formatNumber(totalValue.toFixed(2))}` : "--"}
       </Text>
       <Text style={{ width: '11.11%' }}></Text>
      </View>
     </View>
    )}

    {/* Footer */}
    <View style={styles.footer} fixed>
     <Text>Generated on {moment().format("YYYY-MM-DD HH:mm")} | Reference No: {pdfData?.goods_received_ref_no || "--"}</Text>
    </View>

    {/* Page Number */}
    <Text
     style={styles.pageNumber}
     render={({ pageNumber, totalPages }) => (
      `Page ${pageNumber} of ${totalPages}`
     )}
     fixed
    />
   </Page>
  </Document>
 );
};

export default GoodsReceivedPDF;